import React from 'react';
import { useAuth } from '../contexts/AuthContext';
import { PLANS } from '../lib/plans';
import useCheckout from '../hooks/useCheckout';

const Account = () => {
  const { currentUser, subscription, hasAccess, isTrialing, trialDaysLeft, logout } = useAuth();
  const { startCheckout, loading: checkoutLoading, error: checkoutError } = useCheckout();
  const [selectedPlan, setSelectedPlan] = React.useState('annual');

  const formatDate = (value) => {
    if (!value) return '—';
    const date = value?.toDate ? value.toDate() : new Date(value);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const planLabel = (planId) => {
    const found = PLANS.find((p) => p.id === planId);
    return found ? `${found.label} — ${found.price}${found.period}` : 'Annual';
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <nav className="border-b border-gray-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 stripe-gradient rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path d="M4 3a2 2 0 00-2 2v1a1 1 0 001 1h14a1 1 0 001-1V5a2 2 0 00-2-2H4zM3 8a1 1 0 011-1h12a1 1 0 011 1v5a2 2 0 01-2 2H6a2 2 0 01-2-2V8z" />
              </svg>
            </div>
            <span className="text-2xl font-semibold text-gray-900">Journal Coffee</span>
          </div>
          <div className="flex items-center gap-4">
            <a href="/dashboard" className="text-gray-600 hover:text-gray-900 font-medium">Back to Library</a>
            <button onClick={logout} className="bg-black text-white px-6 py-2 rounded-lg font-medium hover:bg-gray-800 transition-colors">Sign Out</button>
          </div>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold text-gray-900 mb-12">Account Settings</h1>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Profile */}
          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Profile</h2>
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Email address</label>
                <p className="text-gray-900 font-medium">{currentUser?.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Member since</label>
                <p className="text-gray-900">{formatDate(currentUser?.metadata?.creationTime)}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">User ID</label>
                <p className="text-gray-400 text-xs font-mono break-all">{currentUser?.uid}</p>
              </div>
            </div>
          </div>

          {/* Subscription */}
          <div className="bg-white rounded-xl p-8 shadow-sm border border-gray-200">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">Subscription</h2>

            {hasAccess ? (
              <div className="space-y-4">
                {/* Trial status */}
                {isTrialing && (
                  <div className="flex items-start gap-3 p-4 bg-amber-50 rounded-xl border border-amber-200">
                    <svg className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                    </svg>
                    <div>
                      <p className="font-semibold text-amber-800">
                        {trialDaysLeft === 0 ? 'Trial ends today' : `${trialDaysLeft} day${trialDaysLeft === 1 ? '' : 's'} left in your trial`}
                      </p>
                      <p className="text-sm text-amber-600 mt-0.5">
                        Trial ends {formatDate(subscription?.trialEnd)}
                      </p>
                    </div>
                  </div>
                )}

                {/* Plan info */}
                <div className="flex items-center justify-between p-4 bg-green-50 rounded-xl border border-green-200">
                  <div>
                    <p className="font-semibold text-green-800">{planLabel(subscription?.plan)}</p>
                    <p className="text-sm text-green-600">Full access to all research</p>
                  </div>
                  <span className="px-3 py-1 bg-green-100 text-green-800 text-sm font-semibold rounded-full capitalize">
                    {isTrialing ? 'Trial' : subscription?.status}
                  </span>
                </div>

                {subscription?.currentPeriodEnd && !isTrialing && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Next billing date</span>
                    <span className="font-medium text-gray-900">{formatDate(subscription.currentPeriodEnd)}</span>
                  </div>
                )}

                <div className="pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-400">
                    To cancel or update payment, contact{' '}
                    <a href="mailto:support@journalcoffee.com" className="text-indigo-600 hover:underline">
                      support@journalcoffee.com
                    </a>
                  </p>
                </div>
              </div>
            ) : (
              /* No subscription — shouldn't normally be reachable but shown as fallback */
              <div className="space-y-4">
                <p className="text-sm text-gray-500">No active subscription. Set up billing to access all research.</p>
                <div className="grid grid-cols-2 gap-3">
                  {PLANS.map((p) => (
                    <button
                      key={p.id}
                      onClick={() => setSelectedPlan(p.id)}
                      className={`relative rounded-xl border-2 p-4 text-left transition-all ${
                        selectedPlan === p.id ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      {p.badge && (
                        <span className="absolute -top-2.5 left-1/2 -translate-x-1/2 text-xs font-bold text-white stripe-gradient px-2.5 py-0.5 rounded-full whitespace-nowrap">
                          {p.badge}
                        </span>
                      )}
                      <p className="text-xs font-semibold text-gray-600 mb-1">{p.label}</p>
                      <p className="text-xl font-bold text-gray-900">{p.price}<span className="text-xs font-normal text-gray-500">{p.period}</span></p>
                      <p className="text-xs text-gray-500 mt-0.5">{p.billedAs}</p>
                    </button>
                  ))}
                </div>
                {checkoutError && <p className="text-sm text-red-600">{checkoutError}</p>}
                <button
                  onClick={() => startCheckout(selectedPlan)}
                  disabled={checkoutLoading}
                  className="w-full stripe-gradient text-white py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50"
                >
                  {checkoutLoading ? 'Redirecting…' : 'Start 7-day free trial'}
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Account;
